import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, getDocs, collection, query, where } from 'firebase/firestore';
import { auth, db } from './firebase';
import Login from './components/Login';
import Signup from './components/Signup';
import ParentDashboard from './components/ParentDashboard';
import TeacherDashboard from './components/TeacherDashboard';
import AdminDashboard from './components/AdminDashboard';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import StudentDashboard from './components/StudentDashboard';
import ScannerDashboard from './components/ScannerDashboard';
import SplashScreen from './components/SplashScreen';
import './App.css';

function App() {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [role, setRole] = useState(null);
  const [schoolId, setSchoolId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setFirebaseUser(currentUser);
      setError(null);
      if (currentUser) {
        try {
          let userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          // Fallback: the user doc may be keyed by a UUID instead of the auth uid
          if (!userDoc.exists()) {
            let snap = await getDocs(query(collection(db, 'users'), where('id', '==', currentUser.uid)));
            if (snap.empty) {
              snap = await getDocs(query(collection(db, 'users'), where('email', '==', currentUser.email)));
            }
            if (!snap.empty) userDoc = snap.docs[0];
          }
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setRole(userData.role?.toLowerCase() || 'parent');
            setSchoolId(userData.schoolId || null);
          } else {
            setError('No user profile found. Please contact an administrator.');
            setRole(null);
            setSchoolId(null);
          }
        } catch (err) {
          setError('Failed to load user profile: ' + err.message);
          setRole(null);
          setSchoolId(null);
        }
      } else {
        setRole(null);
        setSchoolId(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <>
        <SplashScreen visible={showSplash} />
        <div className="loading-screen">
          <div className="spinner"></div>
        </div>
      </>
    );
  }

  if (error && firebaseUser) {
    return (
      <>
        <SplashScreen visible={showSplash} />
        <div className="loading-screen">
          <div className="error-message">{error}</div>
        </div>
      </>
    );
  }

  const isAuthorized = firebaseUser && role;
  const getDashboardPath = () => {
    if (role === 'parent') return '/dashboard';
    if (role === 'teacher') return '/teacher-dashboard';
    if (role === 'admin') return '/admin-dashboard';
    if (role === 'super admin' || role === 'Super Admin') return '/super-admin-dashboard';
    if (role === 'student') return '/student-dashboard';
    if (role === 'scanner') return '/scanner-dashboard';
    return '/login';
  };

  return (
    <Router>
      <SplashScreen visible={showSplash} />
      <Routes>
        <Route 
          path="/login" 
          element={isAuthorized ? <Navigate to={getDashboardPath()} /> : <Login onLoginSuccess={() => {}} />} 
        />
        <Route 
          path="/signup" 
          element={isAuthorized ? <Navigate to={getDashboardPath()} /> : <Signup onSignupSuccess={() => {}} />} 
        />
        <Route 
          path="/dashboard" 
          element={role === 'parent' ? <ParentDashboard schoolId={schoolId} /> : <Navigate to={getDashboardPath()} />} 
        />
        <Route 
          path="/teacher-dashboard" 
          element={role === 'teacher' ? <TeacherDashboard schoolId={schoolId} /> : <Navigate to={getDashboardPath()} />} 
        />
        <Route 
          path="/admin-dashboard" 
          element={role === 'admin' ? <AdminDashboard schoolId={schoolId} /> : <Navigate to={getDashboardPath()} />} 
        />
        <Route 
          path="/super-admin-dashboard" 
          element={role === 'super admin' || role === 'Super Admin' ? <SuperAdminDashboard schoolId={schoolId} /> : <Navigate to={getDashboardPath()} />} 
        />
        <Route 
          path="/student-dashboard" 
          element={role === 'student' ? <StudentDashboard studentId={firebaseUser?.uid} schoolId={schoolId} /> : <Navigate to={getDashboardPath()} />} 
        />
        <Route 
          path="/scanner-dashboard" 
          element={role === 'scanner' ? <ScannerDashboard /> : <Navigate to={getDashboardPath()} />} 
        />
        <Route 
          path="/" 
          element={<Navigate to={isAuthorized ? getDashboardPath() : "/login"} />} 
        />
      </Routes>
    </Router>
  );
}

export default App;
